import CustomerProfileFields from "./CustomerProfileFields";
import { customerProfilePayload } from "../shared/customerProfile";
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Search,
  User,
  Phone,
  Mail,
  Calendar,
  Save,
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  UserPlus,
  Users,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import "./CustomerCaisseModal.css";

const CustomerCaisseModal = ({
  isOpen,
  onClose,
  onSelectCustomer,
  initialView = "list",
  selectionMode = false,
  selectionContext = "ticket",
}) => {
  const [activeView, setActiveView] = useState(initialView || "list"); // 'list', 'add', 'edit'
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const animationFrameRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const showInitialLoader = loading && customers.length === 0;

  // Formulaire
  const [formData, setFormData] = useState({
    civility: "Mr",
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    birth_date: "",
    notes: "",
  });

  const colors = {
    primary: "#FFB800",
    secondary: "#FF6B35",
    success: "#28A745",
    danger: "#DC3545",
    warning: "#FFC107",
    info: "#17A2B8",
  };

  const resetForm = () => {
    setFormData({
      civility: "Mr",
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      birth_date: "",
      notes: "",
    });
    setSelectedCustomer(null);
    setError(null);
    setSuccess(null);
  };

  // Charger les clients
  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = "/api/customers";
      if (searchTerm.trim()) {
        url = `/api/customers/search/${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Erreur lors du chargement des clients");
      }

      const data = await response.json();
      setCustomers(data);
    } catch (err) {
      setError(err.message);
      console.error("Erreur chargement clients:", err);
    } finally {
      setLoading(false);
    }
  };

  // Effet pour charger les clients et gérer les animations
  useEffect(() => {
    if (isOpen) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setShouldRender(true);
      animationFrameRef.current = requestAnimationFrame(() => {
        setIsAnimating(true);
        setActiveView(initialView || "list");
        if (initialView === "add") {
          resetForm();
        }
        loadCustomers();
      });
    } else {
      setIsAnimating(false);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      hideTimeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        hideTimeoutRef.current = null;
      }, 200);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, [isOpen, initialView]);

  // Effet pour la recherche avec délai
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      loadCustomers();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Gérer la soumission du formulaire
  const readResponseJsonSafe = async (response) => {
    try {
      const text = await response.text();
      if (!text) return null;
      return JSON.parse(text);
    } catch (_) {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!(formData.full_name ?? `${formData.first_name || ""} ${formData.last_name || ""}`).trim()) {
      setError("Le champ Nom & Prénom est obligatoire");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const url = selectedCustomer
        ? `/api/customers/${selectedCustomer.id}`
        : "/api/customers";

      const method = selectedCustomer ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(customerProfilePayload(formData)),
      });

      if (!response.ok) {
        const errorData = await readResponseJsonSafe(response);
        throw new Error(errorData?.error || "Erreur lors de la sauvegarde");
      }

      let savedCustomer = await readResponseJsonSafe(response);
      if (!savedCustomer) {
        savedCustomer = selectedCustomer
          ? { ...selectedCustomer, ...formData }
          : { ...formData };
      }

      setSuccess(
        selectedCustomer
          ? "Client modifié avec succès"
          : "Client créé avec succès",
      );

      // Recharger la liste
      await loadCustomers();

      // Réinitialiser le formulaire et retourner à la liste
      resetForm();
      setActiveView("list");

      // Effacer le message de succès après 3 secondes
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Éditer un client
  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      civility: customer.civility || "Mr",
      first_name: customer.first_name || "",
      last_name: customer.last_name || "",
      phone: customer.phone || "",
      email: customer.email || "",
      birth_date: customer.birth_date || "",
      notes: customer.notes || "",
    });
    setActiveView("edit");
  };

  // Nouveau client
  const handleNew = () => {
    resetForm();
    setActiveView("add");
  };

  // Sélectionner un client (pour l'utiliser dans la commande)
  const handleSelectCustomer = (customer) => {
    if (onSelectCustomer) {
      onSelectCustomer(customer);
    }
    onClose();
  };

  // Fermer le modal
  const handleClose = () => {
    resetForm();
    setActiveView("list");
    setSearchTerm("");
    onClose();
  };

  if (!shouldRender) return null;

  const modal = (
    <div
      className={`customer-modal-overlay fixed inset-0 bg-black transition-all duration-300 ease-out flex items-center justify-center p-4 backdrop-blur-sm ${
        isAnimating ? "bg-opacity-50" : "bg-opacity-0"
      }`}
      style={{ zIndex: 10000 }}
      role="dialog"
      aria-modal="true"
      aria-label={selectionMode ? "Associer un contact" : "Répertoire clients"}
    >
      <div
        className={`customer-modal-content rounded-3xl shadow-2xl w-full ${selectionMode ? "max-w-2xl max-h-[82vh]" : "max-w-4xl max-h-[90vh]"} overflow-hidden transition-all duration-300 ease-out transform border border-white/10 bg-[#0b1328] text-white ${
          isAnimating
            ? "scale-100 opacity-100 translate-y-0"
            : "scale-95 opacity-0 translate-y-4"
        }`}
      >
        {/* Header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-amber-500/15 via-indigo-500/15 to-slate-900 p-6 border-b border-white/10">
          <div className="flex items-start justify-between relative z-10">
            <div className="flex items-center gap-3 text-white">
              <div className="h-12 w-12 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Users size={26} className="text-white" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-amber-200">
                  Clients
                </p>
                <h2 className="text-2xl font-bold">
                  {activeView === "list"
                    ? selectionMode ? "Associer un contact" : "Répertoire"
                    : activeView === "add"
                      ? "Nouveau client"
                      : "Modifier le client"}
                </h2>
                <p className="text-sm text-white/70">
                  {activeView === "list"
                    ? selectionMode
                      ? `Choisissez le contact lié à ${selectionContext === "consignation" ? "cette consignation" : "ce ticket"}.`
                      : "Recherchez un contact ou associez à un ticket"
                    : "Champs essentiels pour une saisie rapide et fiable."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {activeView === "list" && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 border border-white/15 text-white">
                  {customers.length || 0} clients
                </span>
              )}
              <button
                onClick={handleClose}
                className="bg-white/15 hover:bg-white/25 rounded-xl p-3 transition-all duration-200 backdrop-blur-sm border border-white/20"
                title="Fermer"
              >
                <X size={18} className="text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 m-4 rounded-lg animate-fadeIn">
            <div className="flex items-center">
              <AlertCircle size={20} className="text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 m-4 rounded-lg animate-fadeIn">
            <div className="flex items-center">
              <CheckCircle size={20} className="text-green-500 mr-2" />
              <p className="text-green-700">{success}</p>
            </div>
          </div>
        )}

        <div className={`${selectionMode ? "p-4 max-h-[calc(82vh-150px)]" : "p-6 max-h-[calc(90vh-200px)]"} overflow-y-auto bg-[#0d1428]`}>
          {/* Vue Liste */}
          {activeView === "list" && (
            <div className="space-y-5 animate-fadeIn">
              {/* Barre de recherche et actions */}
              <div className="grid gap-3 lg:grid-cols-[2fr_1fr] lg:items-center">
                <div className="relative">
                  <Search
                    size={20}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Nom ou téléphone…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-2xl border border-white/10 bg-[#111a2e] text-white placeholder:text-white/50 shadow-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
                <div className="flex gap-2 w-full lg:justify-end">
                  <button
                    onClick={loadCustomers}
                    className="px-3 py-3 rounded-xl border border-white/10 bg-white/10 text-white hover:bg-white/15 transition flex-1 lg:flex-none flex items-center justify-center"
                    title="Rafraîchir la liste"
                  >
                    <RefreshCw size={18} />
                  </button>
                  <button
                    onClick={handleNew}
                    className="px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold shadow-md hover:shadow-lg transition flex-1 lg:flex-none flex items-center justify-center gap-2"
                  >
                    <UserPlus size={18} />
                    Nouveau
                  </button>
                </div>
              </div>

              {/* Liste des clients */}
              {showInitialLoader ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400 mx-auto"></div>
                  <p className="mt-4 text-white/60">Chargement...</p>
                </div>
              ) : customers.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={64} className="mx-auto text-white/20 mb-4" />
                  <p className="text-xl text-white/70 mb-2">
                    {searchTerm
                      ? "Aucun client trouvé"
                      : "Aucun client enregistré"}
                  </p>
                  <p className="text-white/50">
                    {searchTerm
                      ? "Essayez un autre terme de recherche"
                      : "Commencez par ajouter votre premier client"}
                  </p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {loading && customers.length > 0 && (
                    <div className="flex items-center gap-2 text-xs text-white/60 px-1">
                      <div className="h-3 w-3 rounded-full border-2 border-white/40 border-t-amber-400 animate-spin" />
                      Mise à jour des contacts...
                    </div>
                  )}
                  {customers.map((customer) => {
                    const initials = `${customer.first_name?.[0] || ""}${customer.last_name?.[0] || ""}`.trim() || "👤";
                    return (
                      <div
                        key={customer.id}
                        className="group rounded-2xl border border-white/10 bg-white/5 shadow-sm hover:border-amber-300/40 hover:bg-white/[0.08] transition-all duration-200"
                      >
                        <div className={`flex items-center justify-between gap-3 ${selectionMode ? "p-3" : "p-4"}`}>
                          <div className="flex items-start gap-3">
                            <div className={`${selectionMode ? "h-10 w-10" : "h-12 w-12"} shrink-0 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white font-semibold flex items-center justify-center shadow-inner`}>
                              {initials}
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-white">
                                  {customer.civility} {customer.first_name} {customer.last_name}
                                </h3>
                              </div>
                              <div className="flex items-center gap-3 text-sm text-white/80 flex-wrap">
                                {customer.phone && <span className="inline-flex items-center gap-1">
                                  <Phone size={14} className="text-white/60" />
                                  {customer.phone}
                                </span>}
                                {!selectionMode && customer.email && (
                                  <span className="inline-flex items-center gap-1 text-white/70">
                                    <Mail size={14} className="text-white/60" />
                                    {customer.email}
                                  </span>
                                )}
                              </div>
                              {!selectionMode && customer.birth_date && (
                                <div className="flex items-center gap-1 text-sm text-white/60">
                                  <Calendar size={14} />
                                  {new Date(
                                    customer.birth_date,
                                  ).toLocaleDateString("fr-FR")}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectCustomer(customer);
                              }}
                              className={`${selectionMode ? "min-w-[112px] gap-2 px-3 py-2" : "p-2"} rounded-xl bg-emerald-500/15 text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/25 transition flex items-center justify-center`}
                              title="Associer ce client"
                            >
                              {selectionMode ? <CheckCircle size={16} /> : <Plus size={16} />}
                              <span className={selectionMode ? "text-sm font-semibold" : "sr-only"}>Associer</span>
                            </button>
                            {!selectionMode && <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(customer);
                              }}
                              className="p-2 text-white/60 hover:text-amber-300 hover:bg-amber-500/15 rounded-lg transition-all duration-200"
                              title="Modifier"
                            >
                              <Edit size={18} />
                            </button>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Vue Formulaire (Nouveau/Édition) */}
          {(activeView === "add" || activeView === "edit") && (
            <form onSubmit={handleSubmit} className="space-y-6 animate-fadeIn">
              <CustomerProfileFields value={formData} onChange={setFormData} />

              {/* Boutons */}
              <div className="flex justify-between pt-4">
                <button
                  type="button"
                  onClick={() => setActiveView("list")}
                  className="px-6 py-3 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200"
                >
                  Retour
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-8 py-3 rounded-xl hover:shadow-lg transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <Save size={20} />
                  )}
                  {loading ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
  return typeof document === "undefined" ? modal : createPortal(modal, document.body);
};

export default CustomerCaisseModal;
